import React, { useEffect, useState } from 'react';
import { Checkbox, Chip, FormControlLabel, MenuItem, TextField, Typography } from '@mui/material';
import { useTranslate } from 'react-admin';
import { knowledgeService, type KnowledgeSourceOption } from '../../services/knowledgeService';

export interface KnowledgeSelection {
    use: boolean;
    sourceIds: number[];
}

export const emptyKnowledgeSelection = (): KnowledgeSelection => ({ use: false, sourceIds: [] });

/**
 * Tranche C3 (#489): "Wissensbasis nutzen" switch + optional source
 * restriction for the generator forms. Silently hides itself when the
 * knowledge base is not available (feature off, no sources).
 */
export const KnowledgeSourcePicker: React.FC<{ value: KnowledgeSelection; onChange: (next: KnowledgeSelection) => void; disabled?: boolean }> = ({ value, onChange, disabled }) => {
    const translate = useTranslate();
    const [options, setOptions] = useState<KnowledgeSourceOption[] | null>(null);
    const [available, setAvailable] = useState(true);

    useEffect(() => {
        let cancelled = false;
        knowledgeService.options()
            .then((list) => { if (!cancelled) setOptions(list); })
            .catch(() => { if (!cancelled) { setOptions([]); setAvailable(false); } });
        return () => { cancelled = true; };
    }, []);

    if (!available) return null;

    return (
        <div className="oa-aig-knowledge" data-testid="knowledge-picker">
            <FormControlLabel
                control={<Checkbox size="small" checked={value.use} onChange={(e) => onChange({ ...value, use: e.target.checked })} disabled={disabled || options === null || options.length === 0} />}
                label={translate('aiGenerator.knowledge.use')}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {options !== null && options.length === 0 ? translate('aiGenerator.knowledge.none') : translate('aiGenerator.knowledge.hint')}
            </Typography>
            {value.use && options !== null && options.length > 0 && (
                <TextField
                    select
                    fullWidth
                    size="small"
                    label={translate('aiGenerator.knowledge.sources')}
                    value={value.sourceIds}
                    onChange={(e) => onChange({ ...value, sourceIds: (typeof e.target.value === 'string' ? e.target.value.split(',') : (e.target.value as unknown as (number | string)[])).map(Number).filter((n) => !Number.isNaN(n)) })}
                    slotProps={{ select: { multiple: true, renderValue: (selected) => (selected as number[]).map((id) => options.find((o) => o.id === id)?.title ?? id).join(', ') } }}
                    disabled={disabled}
                    inputProps={{ 'aria-label': translate('aiGenerator.knowledge.sources') }}
                >
                    {options.map((o) => (
                        <MenuItem key={o.id} value={o.id}>
                            <Checkbox size="small" checked={value.sourceIds.includes(o.id)} />
                            {o.title}{o.documentNumber ? ` (${o.documentNumber})` : ''}
                            <Chip size="small" sx={{ ml: 1 }} label={o.scope === 'platform' ? translate('knowledgeBase.platformTitle') : translate('knowledgeBase.pageTitle')} />
                        </MenuItem>
                    ))}
                </TextField>
            )}
        </div>
    );
};

export default KnowledgeSourcePicker;
